/**
 * Ad-hoc AI scheduler (SCHEDULE_TYPE_AI_ADHOC / `formOnly=true`) skips
 * `InterviewManualScheduler`, so `useWeekScheduleFetchV2` never runs and
 * `interviewDataRef.current.userCalendarInfo` stays empty — meaning the existing
 * `SchInterviewerMultiValueLabel` warning icon has nothing to read and the
 * parent cannot gate the submit button on calendar connectivity.
 *
 * This component, rendered as FormBuilder's `FormChild`, reads the `interviewers`
 * field from Formik, fetches calendar availability through the same
 * `/calendar_events` trigger+poll pipeline via `useInterviewerCalendarStatus`,
 * mirrors the results into `interviewDataRef.userCalendarInfo` (matching the
 * shape `useWeekScheduleFetchV2` writes), and notifies the parent via
 * `onCalendarStatusChange` so the footer CTA can show a spinner / disable.
 */
import { useEffect, useMemo } from 'react';
import { useField } from 'formik';
import { useInterviewerCalendarStatus } from '@/components/ats/job-stage-actions/actions/interview/ScheduleWithAi/ScheduleWithAiDrawer/hooks/useInterviewerCalendarStatus';

interface CalendarStatusState {
  isLoading: boolean;
  allConnected: boolean;
  hasSelection: boolean;
}

interface InterviewerCalendarStatusSyncProps {
  interviewDataRef: React.MutableRefObject<any>;
  updateSharedData: (_data: Record<string, any>) => void;
  timezone: string;
  atsJobId?: number | null;
  onCalendarStatusChange?: (_state: CalendarStatusState) => void;
}

export function InterviewerCalendarStatusSync({
  interviewDataRef,
  updateSharedData,
  timezone,
  atsJobId,
  onCalendarStatusChange,
}: InterviewerCalendarStatusSyncProps) {
  const [, meta] = useField('interviewers');
  const rawValue = meta?.value;

  const selectedInterviewerIds = useMemo(() => {
    const ids = Array.isArray(rawValue)
      ? rawValue
      : rawValue !== undefined && rawValue !== null && rawValue !== ''
        ? [rawValue]
        : [];
    return ids
      .map((v) => {
        if (typeof v === 'number') return v;
        if (typeof v === 'string') return Number(v);
        if (v !== null && typeof v === 'object' && 'id' in v) {
          return Number((v as { id: unknown }).id);
        }
        return NaN;
      })
      .filter((n) => Number.isFinite(n));
  }, [rawValue]);

  const { calendarAvailabilityById, isLoading } = useInterviewerCalendarStatus({
    userIds: selectedInterviewerIds,
    timezone,
    atsJobId:
      typeof atsJobId === 'number' ? atsJobId : Number(atsJobId) || undefined,
  });

  /**
   * Mirror results to `interviewDataRef.userCalendarInfo` so
   * `SchInterviewerMultiValueLabel` renders the warning icon. Shape matches
   * `useWeekScheduleFetchV2` so the existing reader path
   * (`userCalendarInfo[id].isCalendarConnected`) just works.
   */
  useEffect(() => {
    const keys = Object.keys(calendarAvailabilityById);
    if (keys.length === 0) return undefined;
    const tid = setTimeout(() => {
      const prev = interviewDataRef?.current?.userCalendarInfo || {};
      const nextInfo: Record<string, any> = { ...prev };
      for (const key of keys) {
        const available = calendarAvailabilityById[key];
        nextInfo[key] = {
          ...(prev[key] || {}),
          id: prev[key]?.id ?? key,
          calendar_available: available,
          isCalendarConnected: available,
        };
      }
      updateSharedData({ userCalendarInfo: nextInfo });
    }, 0);
    return () => clearTimeout(tid);
  }, [calendarAvailabilityById, interviewDataRef, updateSharedData]);

  const hasSelection = selectedInterviewerIds.length > 0;
  const isChecking = hasSelection && isLoading;
  const allConnected = !selectedInterviewerIds.some(
    (id) => calendarAvailabilityById[String(id)] === false
  );

  useEffect(() => {
    onCalendarStatusChange?.({
      isLoading: isChecking,
      allConnected,
      hasSelection,
    });
  }, [isChecking, allConnected, hasSelection, onCalendarStatusChange]);

  return null;
}
